import anime from 'animejs';
import b from 'b_';
import cx from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import debounce from 'lodash/debounce';
import React, { ReactNode } from 'react';

import { CarouselNextButton } from './__button/carousel__button_next';
import { CarouselPrevButton } from './__button/carousel__button_prev';
import CarouselContent from './__content/carousel__content';
import CarouselControls from './__controls/carousel__controls';
import { CarouselFilter } from './__filter/carousel__filter';
import { CarouselContext } from './context';

import './carousel.scss';

type CarouselProps = {
    children: ReactNode[];
    initialItemIndex?: number;
    mix?: string;
    scrollStep: number;
    transitionTimeMs: number;
    allowLinearScroll?: boolean;
    onScroll?: (options: { scroll: number; index: number | undefined }) => void;
    preserveScrollPosition?: boolean;
    managementRef?: React.MutableRefObject<{ scrollToInitialItemIndex: () => void } | undefined>;
};

type CarouselState = {
    scrollValue: number;
    contentWidth: number;
    carouselWidth: number;
    shouldBeScrolledToInitialIndex: boolean;
};

class Carousel extends React.Component<CarouselProps, CarouselState> {
    static Content = CarouselContent;
    static Controls = CarouselControls;
    static Next = CarouselNextButton;
    static Prev = CarouselPrevButton;
    static Filter = CarouselFilter;

    static defaultProps = {
        scrollStep: 500,
        transitionTimeMs: 300,
    };

    state = {
        scrollValue: 0,
        contentWidth: 0,
        carouselWidth: 0,
        shouldBeScrolledToInitialIndex: false,
    };

    componentDidMount() {
        this.erd = elementResizeDetectorMaker({
            strategy: 'scroll',
        });

        if (this.carouselRef) {
            this.erd.listenTo(this.carouselRef, this.handleCarouselSizeChange);
        }

        this.setInitialScroll();

        if (this.props.managementRef) {
            this.props.managementRef.current = { scrollToInitialItemIndex: this.scrollToInitialItemIndex };
        }
    }

    componentDidUpdate(prevProps: CarouselProps, prevState: CarouselState) {
        if (this.contentRef) {
            const { contentWidth } = this.state;
            const domContentWidth = this.contentRef.scrollWidth;

            if (contentWidth !== domContentWidth) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ contentWidth: domContentWidth });
            }

            if (contentWidth !== prevState.contentWidth) {
                if (!this.props.preserveScrollPosition) {
                    this.setInitialScroll();
                }

                if (this.state.shouldBeScrolledToInitialIndex) {
                    this.setInitialScroll();
                    // eslint-disable-next-line react/no-did-update-set-state
                    this.setState({ shouldBeScrolledToInitialIndex: false });
                }
            }
        }
    }

    componentWillUnmount() {
        if (this.carouselRef) {
            this.erd.uninstall(this.carouselRef);
        }

        this.removeContentScrollListener();
    }

    carouselRef?: HTMLElement | null;
    contentRef?: HTMLElement | null;
    itemRefs?: HTMLElement[];
    erd: elementResizeDetectorMaker.Erd;

    getCurrentScrollValue = () => this.contentRef?.scrollLeft || 0;

    getScrollValueByItemIndex = (itemIndex: number) => {
        // чтобы был левый паддинг, когда ширина карусели равна ширине экрана
        if (itemIndex === 0) {
            return 0;
        }

        const item = this.itemRefs?.[itemIndex];
        if (!item) {
            return 0;
        }
        const itemLeft = item.getBoundingClientRect().left;
        const contentLeft = this.contentRef?.getBoundingClientRect().left || 0;
        const contentScrolled = this.getCurrentScrollValue();

        const scrollValue = itemLeft - contentLeft + contentScrolled;

        // не скроллим дальше конца контента
        return Math.min(scrollValue, this.getMaxScrollDistance());
    };

    scrollToInitialItemIndex = () => {
        this.setState({
            shouldBeScrolledToInitialIndex: true,
        });
    };

    getMaxScrollDistance = () => {
        const { contentWidth, carouselWidth } = this.state;

        return Math.max(contentWidth - carouselWidth, 0);
    };

    getPositionStatuses = () => {
        const { contentWidth, scrollValue, carouselWidth } = this.state;

        return {
            inStartPosition: scrollValue === 0,
            inEndPosition: scrollValue + carouselWidth >= contentWidth,
        };
    };

    handleCarouselSizeChange = () => {
        const { carouselWidth } = this.state;

        const newCarouselWidth = this.carouselRef?.offsetWidth || 0;
        if (newCarouselWidth !== 0) {
            this.setState({ carouselWidth: newCarouselWidth }, () => {
                if (carouselWidth === 0) {
                    // если контента не было и он появился нужно установить правильный скролл
                    this.setInitialScroll();
                }
            });
        }
    };

    handleScroll = (scrollDistance: number) => {
        const newScrollValue = this.getNewScrollValueByOffset(scrollDistance);

        this.setScrollAnimated(newScrollValue);
    };

    getNewScrollValueByOffset = (offset: number) => {
        const scrollValue = this.getCurrentScrollValue();

        if (offset > 0) {
            // скролим вправо
            const maxScrollDistance = this.getMaxScrollDistance();
            return Math.min(scrollValue + offset, maxScrollDistance);
        }

        // скролим влево
        return Math.max(scrollValue + offset, 0);
    };

    handleContentScroll = debounce(() => {
        const { onScroll } = this.props;

        if (this.contentRef) {
            const newScrollValue = this.getCurrentScrollValue();
            this.setState({ scrollValue: newScrollValue });

            if (onScroll) {
                const contentLeft = this.contentRef?.getBoundingClientRect().left || 0;
                const firstVisibleItem = this.itemRefs?.findIndex((itemRef) => {
                    const itemRight = itemRef.getBoundingClientRect().right;
                    return itemRight > contentLeft;
                });

                onScroll({ scroll: newScrollValue, index: firstVisibleItem });
            }
        }
    }, 50);

    addContentScrollListener = () => {
        if (this.contentRef && this.props.allowLinearScroll) {
            this.contentRef.addEventListener('scroll', this.handleContentScroll);
        }
    };

    removeContentScrollListener = () => {
        if (this.contentRef && this.props.allowLinearScroll) {
            this.contentRef.removeEventListener('scroll', this.handleContentScroll);
        }
    };

    onContentRef = (ref: HTMLElement | null, itemRefs: HTMLElement[]) => {
        if (ref) {
            this.setState({ contentWidth: ref.scrollWidth });
            this.contentRef = ref;

            this.addContentScrollListener();
        }

        this.itemRefs = itemRefs;
    };

    onCarouselRef = (ref: HTMLElement | null) => {
        if (ref) {
            this.carouselRef = ref;
            this.handleCarouselSizeChange();
        }
    };

    setInitialScroll = () => {
        const { initialItemIndex } = this.props;
        if (typeof initialItemIndex === 'number') {
            const initialScrollValue = this.getScrollValueByItemIndex(initialItemIndex);
            this.setScrollInstant(initialScrollValue);
        } else {
            this.setScrollInstant(0);
        }
    };

    setScrollInstant = (newScrollValue: number) => {
        this.setState({ scrollValue: newScrollValue });

        anime({
            targets: this.contentRef,
            duration: 0,
            scrollLeft: newScrollValue,
        });
    };

    setScrollAnimated = (newScrollValue: number) => {
        const { transitionTimeMs } = this.props;

        this.setState({ scrollValue: newScrollValue });

        anime({
            targets: this.contentRef,
            duration: transitionTimeMs,
            easing: 'easeInOutQuad',
            scrollLeft: newScrollValue,
        });
    };

    render() {
        const { mix, children, scrollStep, allowLinearScroll = false } = this.props;
        const mods = {
            allow_linear_scroll: allowLinearScroll,
        };
        const className = cx(b('carousel', mods), mix);

        return (
            <>
                <CarouselContext.Provider
                    value={{
                        ...this.getPositionStatuses(),
                        scrollContent: this.handleScroll,
                        scrollStep,
                        setContentRef: this.onContentRef,
                    }}
                >
                    <div className={className} ref={this.onCarouselRef}>
                        {children}
                    </div>
                </CarouselContext.Provider>
            </>
        );
    }
}

export default Carousel;
